import { Link } from 'react-router-dom';

interface PainPoint {
  id: string;
  pain: string;
  fix: string;
  href: string;
  linkLabel: string;
}

/** The everyday headaches of a self-managing landlord, each paired with the feature that removes it. */
const PAIN_POINTS: PainPoint[] = [
  {
    id: 'rent',
    pain: 'I scroll my bank app every month to see who paid by Zelle and who is late.',
    fix: 'Your bank feed is read automatically, and each Zelle or Venmo payment is matched to the right lease, with late rent flagged for you.',
    href: '/features/rent-collection',
    linkLabel: 'Rent tracking with Zelle',
  },
  {
    id: 'vacancy',
    pain: 'Every vacancy means posting everywhere and answering ‘is this still available?’ forty times.',
    fix: 'Post once to Craigslist and Facebook Marketplace, and let the AI leasing assistant answer prospects and send showing links.',
    href: '/features/leasing',
    linkLabel: 'Leasing & listings',
  },
  {
    id: 'repairs',
    pain: 'Repair requests arrive as late-night texts, followed by days of phone tag with contractors.',
    fix: 'Tenants report problems with photos, and the AI repair coordinator contacts a vendor from your list and schedules the visit.',
    href: '/features/maintenance',
    linkLabel: 'Maintenance & repairs',
  },
  {
    id: 'taxes',
    pain: 'At tax time I have a pile of receipts and no idea which property each one belongs to.',
    fix: 'Forward receipts to your Resida email address. Each one is filed under the right unit, kept as the original, and categorized for Schedule E.',
    href: '/features/expense-tracking',
    linkLabel: 'Expense tracking by email',
  },
  {
    id: 'slips',
    pain: 'A lease renewal, a late fee, an unsigned document: something always slips through the cracks.',
    fix: 'One list shows everything that needs you, sorted by urgency, and each item clears itself once it is handled.',
    href: '/features/dashboard',
    linkLabel: 'Attention dashboard',
  },
  {
    id: 'deposits',
    pain: 'At move-out, the security deposit comes down to my word against my tenant\'s.',
    fix: 'Move-in and move-out walkthroughs with photos give you a side-by-side record and a deduction report to send.',
    href: '/features/walkthroughs',
    linkLabel: 'Walkthroughs',
  },
];

export function PainPoints(): JSX.Element {
  return (
    <section
      id="pain-points"
      data-testid="landing-pain-points"
      className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--landing-text-primary)] mb-4">
            Sound familiar?
          </h2>
          <p className="text-lg text-[var(--landing-text-muted)] max-w-2xl mx-auto">
            The everyday headaches of managing 1 to 20 rentals on your own, and how Resida takes
            them off your plate.
          </p>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PAIN_POINTS.map((point) => (
            <li
              key={point.id}
              data-testid={`pain-point-${point.id}`}
              className="bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-6 border border-[var(--landing-border)] flex flex-col"
            >
              <p className="text-[var(--landing-text-primary)] font-medium leading-relaxed mb-4">
                &ldquo;{point.pain}&rdquo;
              </p>
              <p className="text-sm text-[var(--landing-text-muted)] leading-relaxed mb-4">
                <span className="font-semibold text-[var(--landing-accent-text)]">With Resida: </span>
                {point.fix}
              </p>
              <Link
                to={point.href}
                className="mt-auto text-sm text-[var(--landing-accent-text)] font-medium hover:text-[var(--landing-accent-text-hover)] transition-colors"
              >
                {point.linkLabel} &rarr;
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
